import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import Divider from '@mui/material/Divider'
import Typography from '@mui/material/Typography'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import GoogleIcon from '../../../shared/components/GoogleIcon'
import type { RegisterFormData } from '../../../shared/utils/types'

// Navegação de browser completa para o backend, que guarda nome/telefone
// num cookie de curta duração e segue para o consentimento do Google — ver
// TenantController#iniciarRegistoGoogle. O admin (nome/email) vem do perfil
// Google depois; por isso o passo 2 (StepAdmin) é saltado neste caminho.
const GOOGLE_SIGNUP_START_URL = `${import.meta.env.VITE_API_URL || ''}/api/public/tenants/register/google/start`

interface Props {
  onNext: () => void
}

export default function StepCompany({ onNext }: Props) {
  const { t } = useTranslation('auth')
  const {
    register,
    trigger,
    getValues,
    formState: { errors },
  } = useFormContext<RegisterFormData>()

  const handleNext = async () => {
    const valid = await trigger(['nome', 'telefone'])
    if (valid) onNext()
  }

  const handleGoogleSignup = async () => {
    const valid = await trigger(['nome', 'telefone'])
    if (!valid) return
    const { nome, telefone } = getValues()
    const params = new URLSearchParams({ nome, telefone })
    window.location.href = `${GOOGLE_SIGNUP_START_URL}?${params.toString()}`
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <TextField
        label={t('register.company.nameLabel')}
        placeholder={t('register.company.namePlaceholder')}
        autoComplete="organization"
        error={!!errors.nome}
        helperText={errors.nome?.message}
        {...register('nome', {
          required: t('register.company.nameRequired'),
          minLength: { value: 2, message: t('register.company.nameTooShort') },
        })}
      />

      <TextField
        label={t('register.company.phoneLabel')}
        type="tel"
        placeholder={t('register.company.phonePlaceholder')}
        autoComplete="tel"
        error={!!errors.telefone}
        helperText={errors.telefone?.message}
        {...register('telefone', {
          required: t('register.company.phoneRequired'),
          minLength: { value: 7, message: t('register.company.phoneInvalid') },
        })}
      />

      <Button
        variant="contained"
        size="large"
        fullWidth
        endIcon={<ArrowForwardIcon />}
        onClick={handleNext}
        sx={{ mt: 0.5 }}
      >
        {t('register.company.continue')}
      </Button>

      <Divider sx={{ my: -1 }}>
        <Typography variant="caption" color="text.secondary">
          {t('register.company.orContinueWith')}
        </Typography>
      </Divider>

      <Button
        variant="outlined"
        size="large"
        fullWidth
        startIcon={<GoogleIcon />}
        onClick={handleGoogleSignup}
        sx={{ textTransform: 'none', borderColor: '#D1D5DB', color: 'text.primary' }}
      >
        {t('register.company.google')}
      </Button>
      <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', mt: -1.5 }}>
        {t('register.company.googleHint')}
      </Typography>
    </Box>
  )
}
